import React, { useEffect, useState } from "react";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import convertNumbThousand from "../../../utils/convertNumbThousand";
import ButtonPrimary from "../../Button/ButtonPrimary";
import { getCarrierLogo, getCarrierInfo } from "../../../utils/flightInfo";
import customTime from "../../../utils/customTime";
import { getAmadeusServiceCharge } from "../Services/helper.amadeus";
import { toast } from "react-toastify";

const FlightResultAmadeus = ({
  flight,
  amaClientRef,
  setLoading,
  query,
  company,
  corporate,
  discount,
  originalPath,
  rateDifference = 1,
  queryURL,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [serviceCharge, setServiceCharge] = useState(0);
  const navigate = useNavigate();

  const searchParams = new URLSearchParams(query);
  const adults = searchParams.get("adults");
  const children = searchParams.get("children");

  useEffect(() => {
    const passengerCount = {
      ADT: +adults || 1,
      CHD: +children || 0,
    };
    const cabinPrice = flight?.travelerPricings;
    const serviceCharge = getAmadeusServiceCharge(
      passengerCount,
      cabinPrice,
      company,
      corporate,
      discount,
      {}
    );
    setServiceCharge(serviceCharge);
  }, [
    adults,
    children,
    company,
    corporate,
    discount,
    flight?.travelerPricings,
  ]);

  if (!flight) return null;

  const parseDuration = (duration) => {
    if (!duration) return "";
    // Amadeus duration format: PT16H50M or PT3H45M
    const hoursMatch = duration.match(/(\d+)H/);
    const minutesMatch = duration.match(/(\d+)M/);
    const daysMatch = duration.match(/(\d+)D/);

    let res = "";
    if (daysMatch) res += `${daysMatch[1]}d `;
    if (hoursMatch) res += `${hoursMatch[1]}h `;
    if (minutesMatch) res += `${minutesMatch[1]}m`;
    return res.trim() || "0m";
  };

  const itineraries = flight.itineraries || [];
  const price = flight.price || {};

  const onSelectFlight = (e) => {
    // e.stopPropagation();
    navigate(`/amadeus-pricing?${query}`, {
      state: {
        offer: flight,
        charges: serviceCharge || 0,
        queryURL: query,
        isRoundTrip: itineraries.length > 1,
        currency: price.currency,
        rateDifference: rateDifference,
        amaClientRef: amaClientRef,
        query: query,
      },
    });
  };

  function totalPrice(price) {
    const airPrice = parseFloat(price);

    let result =
      rateDifference > 1 ? (airPrice * rateDifference).toFixed(2) : airPrice;

    result = parseFloat(result) + serviceCharge;
    return convertNumbThousand(result);
  }

  const renderItinerarySummary = (itinerary, index) => {
    const segments = itinerary.segments || [];
    if (segments.length === 0) return null;

    const firstSegment = segments[0];
    const lastSegment = segments[segments.length - 1];
    const stops = segments.length - 1;
    const carrierLogo = getCarrierLogo(firstSegment.carrierCode);
    const carrierInfo = getCarrierInfo(firstSegment.carrierCode);

    return (
      <div
        key={index}
        className="flex flex-col py-2 border-b md:flex-row md:items-center border-neutral-100 last:border-0"
      >
        {/* Airline Info */}
        <div className="flex items-center md:w-1/4">
          <div className="flex flex-shrink-0 justify-center items-center p-2 w-20 h-16">
            {carrierLogo ? (
              <img
                src={carrierLogo}
                alt="Airline"
                className="object-contain w-full h-full"
              />
            ) : (
              <span className="text-xs font-bold text-neutral-400">
                {firstSegment.carrierCode}
              </span>
            )}
          </div>
          <div className="ml-2 sm:ml-12">
            <p className="text-sm font-bold text-neutral-900">
              {carrierInfo?.Name || firstSegment.carrierCode}
            </p>
            <p className="text-xs font-medium text-neutral-500">
              {firstSegment.carrierCode} {firstSegment.number}
            </p>
          </div>
        </div>

        {/* Flight Path */}
        <div className="flex justify-between items-center px-2 mt-4 md:mt-0 md:w-1/2 md:px-8">
          <div className="flex flex-col">
            <span className="text-xl font-bold tracking-tight text-neutral-900">
              {customTime(firstSegment.departure.at)}
            </span>
            <span className="text-xs font-bold uppercase text-neutral-400">
              {firstSegment.departure.iataCode}
            </span>
          </div>

          <div className="flex flex-col flex-grow items-center px-4 md:px-10">
            <span className="text-[11px] text-neutral-400 mb-1.5 font-bold tracking-wide">
              {parseDuration(itinerary.duration)}
            </span>
            <div className="relative w-full h-[2px] bg-neutral-100 flex items-center justify-center">
              <div className="flex absolute justify-around w-full">
                {segments.slice(0, -1).map((_, i) => (
                  <div
                    key={i}
                    className="w-2 h-2 bg-orange-400 rounded-full border-2 border-white shadow-sm"
                  ></div>
                ))}
              </div>
              {/* Arrow head */}
              <div className="absolute right-0 w-1.5 h-1.5 border-t-2 border-r-2 border-neutral-200 rotate-45"></div>
            </div>
            <span
              className={`text-[10px] mt-1.5 uppercase tracking-widest font-extrabold ${
                stops === 0 ? "text-green-500" : "text-orange-500"
              }`}
            >
              {stops === 0 ? "Direct" : `${stops} Stop${stops > 1 ? "s" : ""}`}
            </span>
          </div>

          <div className="flex flex-col items-end">
            <span className="text-xl font-bold tracking-tight text-neutral-900">
              {customTime(lastSegment.arrival.at)}
            </span>
            <span className="text-xs font-bold uppercase text-neutral-400">
              {lastSegment.arrival.iataCode}
            </span>
          </div>
        </div>

        {/* Price (Desktop) */}
        <div className="hidden flex-col items-end md:flex md:w-1/4">
          {index === 0 && (
            <div className="text-right">
              <span className="text-2xl font-black text-secondary-6000">
                {totalPrice(price.grandTotal || price.total)}{" "}
                <span className="text-sm font-bold text-neutral-400">
                  {price.currency}
                </span>
              </span>
              <p className="text-[10px] text-neutral-400 font-bold uppercase mt-1">
                Total Price
              </p>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderSegmentDetails = (segment, index, allSegments) => {
    const carrierLogo = getCarrierLogo(segment.carrierCode);
    const carrierInfo = getCarrierInfo(segment.carrierCode);
    const baggage =
      flight.travelerPricings?.[0]?.fareDetailsBySegment?.find(
        (f) => f.segmentId === segment.id
      )?.includedCheckedBags?.quantity || 0;

    return (
      <div key={segment.id} className="relative">
        {/* Layover info */}
        {index > 0 && (
          <div className="flex items-center my-6 ml-6 py-2.5 px-4 bg-orange-50/50 rounded-xl border border-orange-100/50">
            <div className="flex items-center text-[11px] font-bold text-orange-600 uppercase tracking-wider">
              <i className="mr-2 text-base las la-clock"></i>
              <span>
                {allSegments[index - 1].arrival.iataCode} Layover:{" "}
                {(() => {
                  const diff = moment(segment.departure.at).diff(
                    moment(allSegments[index - 1].arrival.at)
                  );
                  const dur = moment.duration(diff);
                  return `${Math.floor(dur.asHours())}h ${dur.minutes()}m`;
                })()}
              </span>
            </div>
          </div>
        )}

        <div className="flex items-start">
          {/* Timeline Visual */}
          <div className="flex flex-col items-center mr-6">
            <div className="z-10 w-3 h-3 bg-white rounded-full border-2 border-primary-6000"></div>
            <div className="w-0.5 h-full bg-neutral-200 absolute top-3 bottom-0 left-[5px]"></div>
          </div>

          <div className="flex-grow pb-8">
            <div className="flex flex-col justify-between mb-4 md:flex-row md:items-center">
              <div className="flex items-center">
                <div className="p-1 mr-3 w-8 h-8 bg-white rounded-lg border shadow-sm border-neutral-100">
                  {carrierLogo && (
                    <img
                      src={carrierLogo}
                      alt=""
                      className="object-contain w-full h-full"
                    />
                  )}
                </div>
                <div>
                  <h5 className="text-sm font-bold text-neutral-900">
                    {carrierInfo?.Name || segment.carrierCode} ·{" "}
                    {segment.carrierCode} {segment.number}
                  </h5>
                  <p className="text-[11px] text-neutral-500 font-medium uppercase tracking-tighter">
                    {segment.aircraft?.code &&
                      `Aircraft ${segment.aircraft.code}`}{" "}
                    · Economy
                  </p>
                </div>
              </div>
              <div className="flex items-center px-3 py-1 mt-2 rounded-full md:mt-0 bg-neutral-100/50">
                <i className="las la-briefcase text-neutral-400 mr-1.5"></i>
                <span className="text-[10px] font-bold text-neutral-500 uppercase">
                  {baggage} Bags included
                </span>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-8 p-4 bg-white rounded-2xl border shadow-sm border-neutral-100">
              <div className="space-y-1">
                <p className="text-[10px] text-neutral-400 font-extrabold uppercase tracking-widest">
                  Departure
                </p>
                <p className="text-lg font-black text-neutral-900">
                  {customTime(segment.departure.at)}
                </p>
                <p className="text-xs font-bold text-neutral-600">
                  {segment.departure.iataCode}
                </p>
                <p className="text-[11px] text-neutral-400 font-medium italic">
                  {moment(segment.departure.at).format("ddd, MMM D")}
                </p>
              </div>
              <div className="space-y-1 text-right">
                <p className="text-[10px] text-neutral-400 font-extrabold uppercase tracking-widest">
                  Arrival
                </p>
                <p className="text-lg font-black text-neutral-900">
                  {customTime(segment.arrival.at)}
                </p>
                <p className="text-xs font-bold text-neutral-600">
                  {segment.arrival.iataCode}
                </p>
                <p className="text-[11px] text-neutral-400 font-medium italic">
                  {moment(segment.arrival.at).format("ddd, MMM D")}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div
      className={`group bg-white rounded-2xl border border-neutral-100 shadow-sm hover:shadow-xl hover:border-primary-100 transition-all duration-500 mb-3 overflow-hidden cursor-pointer relative ${
        isOpen ? "ring-2 ring-primary-50" : ""
      }`}
      onClick={() => setIsOpen(!isOpen)}
    >
      <div className="absolute top-0 left-14 z-10 transform -translate-x-1/2 pointer-events-none">
        <div className="flex gap-2 items-center px-4 py-1 rounded-b-lg border-r border-b border-l shadow-sm backdrop-blur border-primary-6000/15 bg-white/80">
          <span className="w-1.5 h-1.5 rounded-full bg-main" />
          <span className="text-[10px] font-bold uppercase tracking-wide text-main">
            Amadeus
          </span>
        </div>
      </div>

      <div className="px-4 mt-2">
        <div className="space-y-2">
          {itineraries.map((itinerary, idx) =>
            renderItinerarySummary(itinerary, idx)
          )}
        </div>

        <div className="flex flex-col gap-2 justify-between pt-1 mt-2 border-t border-neutral-50 md:flex-row md:items-center">
          <div className="flex items-center group/btn">
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors duration-300 ${
                isOpen
                  ? "text-white bg-primary-6000"
                  : "bg-neutral-50 text-neutral-400 group-hover/btn:bg-primary-50 group-hover/btn:text-main"
              }`}
            >
              <i
                className={`las la-angle-down text-md transition-transform duration-500 ${
                  isOpen ? "rotate-180" : ""
                }`}
              ></i>
            </div>
            <span className="ml-3 text-sm font-medium transition-colors duration-300 text-neutral-500 group-hover/btn:text-main">
              {isOpen ? "Hide flight details" : "Show flight details"}
            </span>
          </div>

          <div className="flex gap-2 justify-between items-center py-2 md:justify-end">
            <div className="flex flex-col items-start md:hidden">
              <p className="text-[10px] text-neutral-400 font-bold uppercase tracking-widest mb-1">
                Total Price
              </p>
              <span className="text-2xl font-black text-secondary-6000">
                {totalPrice(price.grandTotal || price.total)}{" "}
                <span className="text-sm font-bold">{price.currency}</span>
              </span>
            </div>

            <button
              className="w-full md:w-auto px-4 py-2 rounded-2xl shadow-xl shadow-primary-500/10 hover:shadow-primary-500/20 transform hover:-translate-y-0.5 transition-all duration-300 bg-main text-white font-semibold"
              onClick={onSelectFlight}
            >
              Select
            </button>
          </div>
        </div>
      </div>

      {isOpen && (
        <div className="p-6 border-t duration-500 bg-neutral-50/50 md:p-10 border-neutral-100 animate-in fade-in slide-in-from-top-4">
          {itineraries.map((itinerary, idx) => (
            <div
              key={idx}
              className={
                idx > 0
                  ? "mt-12 pt-12 border-t border-dashed border-neutral-200"
                  : ""
              }
            >
              <div className="flex items-center mb-4">
                <div className="w-1.5 h-6 bg-primary-6000 rounded-full mr-4"></div>
                <h4 className="text-base font-black tracking-widest uppercase text-neutral-900">
                  {idx === 0 ? "Outbound Journey" : "Inbound Journey"}
                </h4>
                <span className="ml-auto text-[11px] font-bold text-neutral-400 bg-white px-3 py-1 rounded-full border border-neutral-100 shadow-sm">
                  {itinerary.segments.length}{" "}
                  {itinerary.segments.length > 1 ? "Segments" : "Segment"}
                </span>
              </div>
              <div className="ml-2 max-w-3xl">
                {itinerary.segments.map((segment, sIdx) =>
                  renderSegmentDetails(segment, sIdx, itinerary.segments)
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default FlightResultAmadeus;
